import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './Timeline.css';

const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

type TimelineTyp = 'audit' | 'notiz' | 'aufgabe_erstellt' | 'aufgabe_erledigt' | 'dokument';

interface TimelineItem {
  typ: TimelineTyp;
  icon: string;
  titel: string;
  detail: string;
  autor: string;
  zeitpunkt: string | null;
}

interface TimelineProps {
  entitaetTyp: string;
  entitaetId: number;
  authToken: string;
}

function formatDatum(iso: string | null): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function getDatumGruppe(iso: string | null): string {
  if (!iso) return 'Unbekannt';
  try {
    const d = new Date(iso);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    const isSameDay = (a: Date, b: Date) =>
      a.getDate() === b.getDate() &&
      a.getMonth() === b.getMonth() &&
      a.getFullYear() === b.getFullYear();

    if (isSameDay(d, today)) return 'Heute';
    if (isSameDay(d, yesterday)) return 'Gestern';
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return iso;
  }
}

const TYP_LABEL: Record<string, string> = {
  audit: 'Systemereignis',
  notiz: 'Notiz',
  aufgabe_erstellt: 'Aufgabe erstellt',
  aufgabe_erledigt: 'Aufgabe erledigt',
  dokument: 'Dokument',
};

const TYP_COLOR: Record<string, string> = {
  audit: 'var(--ink-2)',
  notiz: 'var(--green-900)',
  aufgabe_erstellt: 'var(--gold)',
  aufgabe_erledigt: 'var(--green-500)',
  dokument: 'var(--green-900)',
};

const Timeline: React.FC<TimelineProps> = ({ entitaetTyp, entitaetId, authToken }) => {
  const [items, setItems] = useState<TimelineItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(
        `${API_URL}/api/timeline?entitaet_typ=${entitaetTyp}&entitaet_id=${entitaetId}`,
        { headers }
      );
      setItems(res.data);
    } catch {
      setError('Timeline konnte nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entitaetTyp, entitaetId, authToken]);

  useEffect(() => { load(); }, [load]);

  // Gruppierung nach Datum
  const gruppen: { label: string; items: TimelineItem[] }[] = [];
  const gruppenMap = new Map<string, TimelineItem[]>();

  for (const item of items) {
    const gruppe = getDatumGruppe(item.zeitpunkt);
    if (!gruppenMap.has(gruppe)) {
      gruppenMap.set(gruppe, []);
    }
    gruppenMap.get(gruppe)!.push(item);
  }

  for (const [label, groupItems] of gruppenMap.entries()) {
    gruppen.push({ label, items: groupItems });
  }

  return (
    <div className="tl-container">
      <div className="tl-header">
        <h3 className="tl-title">Aktivitäten</h3>
        <button type="button" className="tl-refresh-btn" onClick={load} disabled={loading}>
          {loading ? '⏳' : '↻'} Aktualisieren
        </button>
      </div>

      {error && <div className="tl-error">{error}</div>}

      {loading ? (
        <div className="tl-loading">Timeline wird geladen…</div>
      ) : items.length === 0 ? (
        <div className="tl-empty">
          
          <p>Noch keine Aktivitäten vorhanden.</p>
        </div>
      ) : (
        <div className="tl-feed">
          {gruppen.map(gruppe => (
            <div key={gruppe.label} className="tl-gruppe">
              <div className="tl-datum-label">{gruppe.label}</div>
              {gruppe.items.map((item, idx) => (
                <div key={idx} className="tl-item">
                  <div className="tl-line-container">
                    <div
                      className="tl-icon-dot"
                      style={{ background: TYP_COLOR[item.typ] || 'var(--ink-2)' }}
                    >
                      {item.icon}
                    </div>
                    <div className="tl-connector" />
                  </div>
                  <div className="tl-content">
                    <div className="tl-item-header">
                      <span className="tl-item-titel">{item.titel}</span>
                      <span
                        className="tl-typ-badge"
                        style={{ background: TYP_COLOR[item.typ] || 'var(--ink-2)' }}
                      >
                        {TYP_LABEL[item.typ] || item.typ}
                      </span>
                    </div>
                    {item.detail && (
                      <div className="tl-detail">
                        {item.detail.length > 200
                          ? item.detail.slice(0, 200) + '…'
                          : item.detail}
                      </div>
                    )}
                    <div className="tl-meta">
                      <span className="tl-autor">{item.autor}</span>
                      <span className="tl-zeit">{formatDatum(item.zeitpunkt)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Timeline;
